import { useState, type ChangeEvent } from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { Button, Dialog, Select, Switch, TextField } from '@nyce/admin-kit/ui';
import { ErrorBox } from '../../../components/common';
import {
  monitorsFor,
  type BuddySystemRef,
  type BuddyTenantRef,
  type MonitorScope,
  type MonitorTestResult,
} from '../../api/monitors';
import { describeCron, validateCron } from './cron';
import {
  COMBINE_MODES,
  NOTIFY_MODES,
  RUN_MODES,
  SOURCE_TYPES,
  emptySource,
  systemsFor,
  toInput,
  type MonitorForm,
  type SourceForm,
} from './form';
import { TestResultView } from './TestResultView';

const SCOPES = [
  { value: 'global', label: 'Global: every system' },
  { value: 'tenant', label: 'Tenant: every system of a tenant' },
  { value: 'system', label: 'System: one system' },
];

export interface MonitorEditorProps {
  target: string;
  initial: MonitorForm;
  tenants: BuddyTenantRef[];
  systems: BuddySystemRef[];
  onSave: (f: MonitorForm) => Promise<void>;
  onClose: () => void;
}

/** Create or edit a monitor; "Test draft" runs the sources on one system without saving (no alerts, no email). */
export function MonitorEditor({ target, initial, tenants, systems, onSave, onClose }: MonitorEditorProps) {
  const [f, setF] = useState<MonitorForm>(initial);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'save' | 'test' | null>(null);
  const [test, setTest] = useState<MonitorTestResult | null>(null);
  const [testSystem, setTestSystem] = useState(initial.systemId);
  const set = <K extends keyof MonitorForm>(k: K, v: MonitorForm[K]) => setF((cur) => ({ ...cur, [k]: v }));
  const text = (
    k:
      | 'name'
      | 'description'
      | 'cronExpression'
      | 'instructions'
      | 'maxToolCalls'
      | 'gateInstructions'
      | 'logSince'
      | 'reportSpecText'
      | 'notifyEmails'
  ) => ({
    value: f[k],
    onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => set(k, e.target.value),
  });
  const cronError = validateCron(f.cronExpression);
  const testable = systemsFor(f, systems);
  const editSource = (key: number, patch: Partial<SourceForm>) =>
    set(
      'sources',
      f.sources.map((s) => (s.key === key ? { ...s, ...patch } : s))
    );
  const moveSource = (i: number, dir: -1 | 1) => {
    const next = [...f.sources];
    [next[i], next[i + dir]] = [next[i + dir], next[i]];
    set('sources', next);
  };

  const save = async () => {
    const r = toInput(f);
    if ('error' in r) return setError(r.error);
    setBusy('save');
    setError(null);
    try {
      await onSave(f);
      onClose();
    } catch (e) {
      setError((e as Error).message);
      setBusy(null);
    }
  };
  const testDraft = async () => {
    const r = toInput(f);
    if ('error' in r) return setError(r.error);
    const systemId = f.scope === 'system' ? f.systemId : testSystem;
    if (!systemId) return setError('Choose a system to test the draft on.');
    setBusy('test');
    setError(null);
    try {
      setTest(await monitorsFor(target).testDraft(r.body, systemId));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={f.id ? `Edit ${initial.name || 'monitor'}` : 'New monitor'}
      maxWidth={880}
      footer={
        <div className="bd-toolbar">
          {f.scope !== 'system' && (
            <Select
              aria-label="Test on system"
              value={testSystem}
              onChange={setTestSystem}
              placeholder="Test on system…"
              options={testable.map((s) => ({ value: s.id, label: s.name }))}
            />
          )}
          <Button
            variant="ghost"
            onClick={() => void testDraft()}
            loading={busy === 'test'}
            disabled={!!busy}
          >
            Test draft
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => void save()}
            loading={busy === 'save'}
            disabled={!!busy || !f.name.trim() || !!cronError}
          >
            Save
          </Button>
        </div>
      }
    >
      <ErrorBox error={error} />
      {!!f.assumptions?.length && (
        <div className="bm-notice" data-testid="assumptions">
          <b>Assumptions the generator made: check them.</b>
          <ul>
            {f.assumptions.map((a, i) => (
              <li key={i}>{a}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="nyk-form">
        <TextField label="Name" required {...text('name')} />
        <TextField label="Description" {...text('description')} />
        <Select
          label="Scope"
          value={f.scope}
          onChange={(v) => set('scope', v as MonitorScope)}
          options={SCOPES}
          disabled={!!f.id}
          hint={f.id ? 'Fixed once created.' : undefined}
        />
        {f.scope === 'tenant' && (
          <Select
            label="Tenant"
            value={f.tenantId}
            onChange={(v) => set('tenantId', v)}
            options={tenants.map((t) => ({ value: t.id, label: t.name }))}
            placeholder="Select tenant…"
            disabled={!!f.id}
          />
        )}
        {f.scope === 'system' && (
          <Select
            label="System"
            value={f.systemId}
            onChange={(v) => set('systemId', v)}
            options={systems.map((s) => ({ value: s.id, label: `${s.name} (${s.id})` }))}
            placeholder="Select system…"
            disabled={!!f.id}
          />
        )}
        {f.scope === 'global' && <div />}
        <TextField
          label="Schedule (5-field cron, UTC)"
          required
          {...text('cronExpression')}
          error={cronError ?? undefined}
          hint={
            cronError ? undefined : <span data-testid="cron-preview">{describeCron(f.cronExpression)}</span>
          }
        />
        <div className="nyk-ui-field nyk-switch-field">
          <span>Enabled</span>
          <Switch
            checked={f.enabled}
            onChange={(v) => set('enabled', v)}
            label={f.enabled ? 'Runs on schedule' : 'Paused'}
          />
        </div>
        <Select
          label="Run mode"
          value={f.runMode}
          onChange={(v) => set('runMode', v)}
          options={RUN_MODES}
          disabled={f.fromSavedResponse}
        />
        <TextField
          label="Max tool calls (investigation budget)"
          type="number"
          min={0}
          {...text('maxToolCalls')}
        />
        <Select
          label="Combine sources"
          value={f.combineMode}
          onChange={(v) => set('combineMode', v)}
          options={COMBINE_MODES}
        />
        <TextField label="Log lookback (Cloud Logging sources)" placeholder="1h" {...text('logSince')} />
        <div className="nyk-span2">
          <TextField
            label="Instructions for the investigation agent"
            multiline
            rows={4}
            {...text('instructions')}
          />
        </div>
        {f.combineMode === 'llm_gate' && (
          <div className="nyk-span2">
            <TextField
              label="Gate instructions (what makes findings of several sources the same issue)"
              multiline
              rows={3}
              {...text('gateInstructions')}
            />
          </div>
        )}
        {f.runMode === 'report' && (
          <div className="nyk-span2">
            <TextField
              label="Report spec (JSON: title, rows of insights, table or gauge)"
              multiline
              rows={8}
              className="oph-json"
              placeholder={
                '{ "title": "…", "rows": [{ "type": "insights", "template": "{{COUNT}} items" }, { "type": "table" }] }'
              }
              {...text('reportSpecText')}
            />
          </div>
        )}
      </div>

      {f.fromSavedResponse ? (
        <p className="nyk-ui-hint">
          This monitor replays a saved chat answer (its owner made it from the chat), so it has no sources to
          edit.
        </p>
      ) : (
        <>
          <div className="nyk-card-head">
            <h3 className="bm-section-title">Sources</h3>
            <Button
              size="sm"
              variant="ghost"
              icon={<Plus size={14} />}
              onClick={() => set('sources', [...f.sources, emptySource()])}
            >
              Add source
            </Button>
          </div>
          {f.sources.map((s, i) => (
            <section key={s.key} className="bm-source" data-source-index={i}>
              <div className="nyk-form">
                <TextField
                  label="Label"
                  aria-label={`Source ${i + 1} label`}
                  value={s.label}
                  onChange={(e) => editSource(s.key, { label: e.target.value })}
                  placeholder="late_orders"
                />
                <Select
                  label="Type"
                  aria-label={`Source ${i + 1} type`}
                  value={s.type}
                  onChange={(v) => editSource(s.key, { type: v })}
                  options={SOURCE_TYPES}
                />
                {s.type !== 'llm' && (
                  <div className="nyk-span2">
                    <TextField
                      label={s.type === 'gcp_logs' ? 'Cloud Logging filter condition' : 'Literal query'}
                      aria-label={`Source ${i + 1} query`}
                      multiline
                      rows={3}
                      className="oph-json"
                      value={s.query}
                      onChange={(e) => editSource(s.key, { query: e.target.value })}
                      placeholder={
                        s.type === 'gcp_logs'
                          ? 'severity>=ERROR AND textPayload=~"timeout"'
                          : 'select Number, Client.Code from CustomerOrder where … and DateAdded >= {{LAST_RUN}}'
                      }
                    />
                  </div>
                )}
                {s.type === 'llm' && (
                  <div className="nyk-span2">
                    <TextField
                      label="Detection instructions (what counts as at risk)"
                      aria-label={`Source ${i + 1} instructions`}
                      multiline
                      rows={3}
                      value={s.instructions}
                      onChange={(e) => editSource(s.key, { instructions: e.target.value })}
                    />
                  </div>
                )}
                <TextField
                  label="Entity key columns (comma separated)"
                  aria-label={`Source ${i + 1} entity key columns`}
                  value={s.entityKeyColumns}
                  onChange={(e) => editSource(s.key, { entityKeyColumns: e.target.value })}
                  placeholder="Number"
                />
                {s.type === 'gcp_logs' && (
                  <TextField
                    label="Log lookback for this source"
                    aria-label={`Source ${i + 1} log lookback`}
                    value={s.logSince}
                    onChange={(e) => editSource(s.key, { logSince: e.target.value })}
                    placeholder="the monitor's"
                  />
                )}
              </div>
              <div className="nyk-row-actions">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<ArrowUp size={14} />}
                  onClick={() => moveSource(i, -1)}
                  disabled={i === 0}
                >
                  Up
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<ArrowDown size={14} />}
                  onClick={() => moveSource(i, 1)}
                  disabled={i === f.sources.length - 1}
                >
                  Down
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  icon={<Trash2 size={14} />}
                  onClick={() =>
                    set(
                      'sources',
                      f.sources.filter((x) => x.key !== s.key)
                    )
                  }
                  disabled={f.sources.length === 1}
                >
                  Remove
                </Button>
              </div>
            </section>
          ))}
        </>
      )}

      <h3 className="bm-section-title">Notifications</h3>
      <div className="nyk-form">
        <TextField
          label="Recipients (comma separated)"
          placeholder="ops@example.com"
          {...text('notifyEmails')}
        />
        <Select
          label="Notify"
          value={f.notifyMode}
          onChange={(v) => set('notifyMode', v)}
          options={NOTIFY_MODES}
        />
      </div>

      {test && (
        <section data-testid="draft-test">
          <div className="nyk-card-head">
            <h3 className="bm-section-title">Draft test</h3>
            <Button size="sm" variant="ghost" onClick={() => setTest(null)}>
              Hide
            </Button>
          </div>
          <TestResultView result={test} />
        </section>
      )}
    </Dialog>
  );
}
